import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch } from '../hooks/redux'
import { useCompatRouter } from '../hooks/useCompatRouter'
import { getSupabaseBrowserClient, hasSupabaseBrowserConfig } from '../lib/supabaseClient'
import { setSession } from '../store/slices/authSlice'
import { getCurrentPortalSession } from '../services/guestPortalSupabase'

const MIN_PASSWORD_LENGTH = 6

const ResetPasswordPage = () => {
  const dispatch = useAppDispatch()
  const router = useCompatRouter()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCheckingSession, setIsCheckingSession] = useState(true)
  const [hasRecoverySession, setHasRecoverySession] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!hasSupabaseBrowserConfig()) {
      setError('Password recovery is not configured for this environment.')
      setIsCheckingSession(false)
      return
    }

    const supabase = getSupabaseBrowserClient()
    let active = true

    const resolveRecoverySession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession()

        if (!active) {
          return
        }

        if (error) {
          setError(error.message)
          setHasRecoverySession(false)
          return
        }

        setHasRecoverySession(Boolean(data.session))
      } catch (error) {
        if (!active) {
          return
        }

        setError(error instanceof Error ? error.message : 'Unable to validate your reset link.')
        setHasRecoverySession(false)
      } finally {
        if (active) {
          setIsCheckingSession(false)
        }
      }
    }

    void resolveRecoverySession()

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) {
        return
      }

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setHasRecoverySession(Boolean(session))
        setError('')
        setIsCheckingSession(false)
      }

      if (event === 'SIGNED_OUT') {
        setHasRecoverySession(false)
      }
    })

    return () => {
      active = false
      data.subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (!hasRecoverySession) {
      setError('Open the reset link from your email again, then set your new password here.')
      return
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError('Password must be at least 6 characters.')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setIsSubmitting(true)

    try {
      const supabase = getSupabaseBrowserClient()
      const { error: updateError } = await supabase.auth.updateUser({ password })

      if (updateError) {
        throw updateError
      }

      const sessionState = await getCurrentPortalSession()
      if (sessionState) {
        dispatch(setSession(sessionState))
      }

      setMessage('Password updated. Redirecting you back to sign-in.')

      window.setTimeout(() => {
        void router.replace('/booking-system/login')
      }, 1200)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unable to update your password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="admin-login-theme min-h-screen px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl items-center justify-center">
        <div className="admin-login-panel">
          <aside className="admin-login-aside">
            <div>
              <span className="admin-login-kicker">Account Recovery</span>
              <h1 className="text-4xl font-bold leading-tight text-white">Set a new password and get back into Marina Glen.</h1>
              <p className="mt-4 max-w-md text-base text-white/80">
                This page completes the secure Supabase recovery flow for guest and admin accounts.
              </p>
              <ul className="admin-login-feature-list">
                <li>Works with Supabase password recovery emails</li>
                <li>Keeps account access on the live booking domain</li>
                <li>Redirects you back to sign-in after success</li>
              </ul>
            </div>
            <div className="text-sm text-white/70">
              <p>Need a fresh reset email?</p>
              <Link to="/booking-system/login" className="mt-2 inline-flex text-sm font-semibold text-white hover:text-white/80 transition-colors">
                Return to sign in
              </Link>
            </div>
          </aside>

          <section className="admin-login-content">
            <div className="mb-8">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Password Reset</p>
              <h2 className="mt-3 text-4xl font-bold text-black">Create a New Password</h2>
              <p className="mt-3 text-base text-slate-600">
                {isCheckingSession
                  ? 'Validating your recovery link.'
                  : hasRecoverySession
                    ? 'Enter your new password below.'
                    : 'Open the reset link from your email to continue.'}
              </p>
            </div>

            {error && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {message && (
              <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-sm text-emerald-700">{message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="mb-2 block text-sm font-semibold text-slate-700">
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value)
                    setError('')
                    setMessage('')
                  }}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="At least 6 characters"
                  disabled={!hasRecoverySession || isCheckingSession || isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="mb-2 block text-sm font-semibold text-slate-700">
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value)
                    setError('')
                    setMessage('')
                  }}
                  className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3.5 text-slate-900 shadow-sm transition-all duration-300 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-100"
                  placeholder="Repeat your new password"
                  disabled={!hasRecoverySession || isCheckingSession || isSubmitting}
                />
              </div>

              <button
                type="submit"
                disabled={!hasRecoverySession || isCheckingSession || isSubmitting}
                className="w-full rounded-2xl bg-slate-950 px-4 py-3.5 font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? 'Updating Password...' : 'Update Password'}
              </button>
            </form>

            <div className="mt-8 flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Need another recovery email?{' '}
                <Link to="/booking-system/login" className="font-semibold text-sky-700 hover:text-sky-800">
                  Return to sign in
                </Link>
              </p>
              <Link
                to="/booking-system"
                className="font-medium text-slate-500 transition-colors hover:text-slate-800"
              >
                ← Back to Home
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage